import { useCallback, useMemo, useState, useRef, useEffect, type JSX } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import {
  Bell,
  LogOut,
  ChevronDown,
  Users,
  Key,
  Building2,
  PhoneCall,
  MessageSquare,
  Mail,
  Zap,
  BarChart3,
  Shield,
  FileText,
  Megaphone,
  Mic,
  // Cog,
  List,
  BookOpen,
  Sparkles,
  ClipboardCheck,
  CalendarClock,
  Cable } from 'lucide-react'
import _RequirePermission from '../auth/RequirePermission.js'
import NavSearch, { type NavSearchItem } from './NavSearch.js'
import TimezoneMenuItem from '../common/TimezoneMenuItem.js'
import { useAuthStore } from '../../store/authStore.js'
import { api } from '../../api/client.js'
import { loginPathFor } from '../../auth/loginPath.js'
import voz360Logo from '../../assets/voz360.png'
import { BrowserNotificationPrompt } from '../notifications/BrowserNotificationPrompt.js'

const EMPTY_PERMS: string[] = []

type NavLeaf = { to: string; label: string; requirePermission?: string }
type NavSection = {
  sectionLabel: string
  icon: React.ReactNode
  requirePermission?: string | string[]
  items: NavLeaf[]
}

const NAV: NavSection[] = [
  {
    sectionLabel: 'Tenant Management',
    icon: <Building2 className="w-5 h-5" />,
    items: [
      { to: '/admin/tenant/org-details', label: 'Organization Details', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/location', label: 'Location', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/branch', label: 'Branch', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/department', label: 'Department', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/break-codes', label: 'Break Codes', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/destination-types', label: 'Destination Types', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/password-policy', label: 'Password Policy', requirePermission: 'admin:settings:view' },
      { to: '/admin/tenant/user-portal-ip-whitelist', label: 'User Portal IP Whitelist', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'User Management',
    icon: <Users className="w-5 h-5" />,
    items: [
      { to: '/admin/user-management/users', label: 'Users', requirePermission: 'admin:users:view' },
      { to: '/admin/user-management/teams', label: 'Teams', requirePermission: 'admin:users:view' },
      { to: '/admin/user-management/skills', label: 'Skills', requirePermission: 'admin:agents:view' },
      { to: '/admin/user-management/approvers', label: 'Approvers', requirePermission: 'admin:users:view' },
      { to: '/admin/mfa', label: 'Multi-Factor Auth (MFA)' },
    ],
  },
  {
    sectionLabel: 'RBAC',
    icon: <Key className="w-5 h-5" />,
    items: [
      { to: '/admin/roles', label: 'Roles', requirePermission: 'admin:roles:view' },
      { to: '/admin/permissions', label: 'Permissions', requirePermission: 'admin:roles:view' },
      { to: '/admin/permissions/audit', label: 'Audit Log', requirePermission: 'admin:roles:view' },
      { to: '/admin/mfa', label: 'Multi-Factor Auth (MFA)' },
    ],
  },
  {
    sectionLabel: 'Telephony Management',
    icon: <PhoneCall className="w-5 h-5" />,
    items: [
      { to: '/admin/advance-call-flows', label: 'Advance Call Flow', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/audio-files', label: 'Audio Files', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/blacklist-callers', label: 'Blacklist Callers', requirePermission: 'admin:settings:view' },
      { to: '/admin/conferences', label: 'Conference', requirePermission: 'admin:settings:view' },
      { to: '/admin/contact-center-queues', label: 'Contact Center Queue', requirePermission: 'admin:settings:view' },
      { to: '/admin/voice-contact-management/disposition-groups', label: 'Disposition Groups', requirePermission: 'admin:settings:view' },
      { to: '/admin/voice-contact-management/dispositions', label: 'Dispositions', requirePermission: 'admin:settings:view' },
      { to: '/admin/external-call-forwarding', label: 'External Call Forwarding', requirePermission: 'admin:settings:view' },
      { to: '/admin/fifo-queues', label: 'FIFO Queues', requirePermission: 'admin:settings:view' },
      { to: '/admin/follow-me', label: 'Follow Me', requirePermission: 'agents:view' },
      { to: '/admin/hot-desking', label: 'Hot Desking', requirePermission: 'agents:view' },
      { to: '/admin/ivr', label: 'IVR', requirePermission: 'admin:settings:view' },
      { to: '/admin/inbound-numbers', label: 'Inbound Numbers', requirePermission: 'admin:settings:view' },
      { to: '/admin/outbound-call-routing', label: 'Outbound Call Routing', requirePermission: 'admin:settings:view' },
      { to: '/admin/number-pool', label: 'Outbound Caller ID', requirePermission: 'admin:extensions:view' },
      { to: '/admin/paging', label: 'Paging', requirePermission: 'agents:view' },
      { to: '/admin/telephone-management/play-n-go', label: 'Play N Go', requirePermission: 'admin:settings:view' },
      { to: '/admin/ring-groups', label: 'Ring Groups', requirePermission: 'admin:settings:view' },
      { to: '/admin/sip-trunks', label: 'SIP Trunks', requirePermission: 'admin:extensions:view' },
      { to: '/admin/telephone-management/special-inbound-routes', label: 'Special Inbound Routes', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/speed-dial', label: 'Speed Dial', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/time-conditions', label: 'Time Conditions', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/transfer', label: 'Transfer', requirePermission: 'admin:settings:view' },
      { to: '/admin/telephone-management/vip-callers', label: 'VIP Callers', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    // AI Center — deliberately its own top-level section rather than an entry
    // under Telephony Management, where "AI Configuration" originally sat.
    // Model routing applies to every channel (chat, email, WhatsApp, voice)
    // and this section grows into the wider AI Center (agents, knowledge,
    // prompts, evaluation), none of which is telephony.
    sectionLabel: 'AI Center',
    icon: <Sparkles className="w-5 h-5" />,
    items: [
      { to: '/admin/ai-overview', label: 'Dashboard', requirePermission: 'admin:extensions:view' },
      // Configuration is a top-level page combining AI Configuration + Safety sub-tabs.
      { to: '/admin/ai-config', label: 'Configuration', requirePermission: 'admin:extensions:view' },
      { to: '/admin/ai-agents', label: 'AI Agents', requirePermission: 'ai:agents_view' },
      { to: '/admin/ai-usage', label: 'Usage & Budget', requirePermission: 'admin:extensions:view' },
      { to: '/admin/ai-audit', label: 'Audit Log', requirePermission: 'admin:extensions:view' },
      { to: '/admin/ai-testing', label: 'Testing', requirePermission: 'admin:extensions:view' },
      { to: '/admin/ai-live-assist', label: 'Live Call Assist', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-agents', label: 'Voice Agents', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-templates', label: 'Voice Templates', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-tools', label: 'Voice Tools', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-widgets', label: 'Voice Widgets', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-settings', label: 'Voice Settings', requirePermission: 'admin:settings:view' },
      { to: '/admin/ai-voice-assistant', label: 'Agent Voice Assistant', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    // Quality Management — QM scoring forms. The default form is filled in
    // automatically by the QM Auditor AI agent from call transcripts.
    sectionLabel: 'Quality Management',
    icon: <ClipboardCheck className="w-5 h-5" />,
    items: [
      { to: '/admin/quality-management/forms', label: 'QM Forms', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    // Workforce Management — forecast demand, build and publish schedules,
    // watch adherence and handle agent time-off / swap requests.
    sectionLabel: 'Workforce Management',
    icon: <CalendarClock className="w-5 h-5" />,
    items: [
      { to: '/admin/workforce-management/schedule', label: 'Schedule', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/forecasts', label: 'Forecasts', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/adherence', label: 'Adherence', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/requests', label: 'Requests', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/shift-templates', label: 'Shift Templates', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/activity-types', label: 'Activity Types', requirePermission: 'admin:settings:view' },
      { to: '/admin/workforce-management/settings', label: 'Settings', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Conversation Management',
    icon: <List className="w-5 h-5" />,
    requirePermission: ['admin:settings:view', 'platform:audit_view'],
    items: [
      { to: '/admin/conversation-management/types', label: 'Types', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/categories', label: 'Categories', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/subcategories', label: 'Subcategories', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/status', label: 'Status', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/states', label: 'States', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/impacts', label: 'Impacts', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/urgencies', label: 'Urgency', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/priorities', label: 'Priorities', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/complexities', label: 'Complexity', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/quick-reply-categories', label: 'QK Reply Categories', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/quick-reply-subcategories', label: 'QK Reply Subcategories', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/quick-replies', label: 'Quick Replies', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/approval-states', label: 'Approval States', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/approval-levels', label: 'Approval Levels', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/tags', label: 'Tags', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/additional-properties', label: 'Additional Properties', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/dependencies', label: 'Dependencies', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/checklists', label: 'Checklists', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/forms', label: 'Conversation Form', requirePermission: 'admin:settings:view' },
      { to: '/admin/conversation-management/root-cause-analysis', label: 'Root Cause Analysis', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Channels',
    icon: <MessageSquare className="w-5 h-5" />,
    items: [
      { to: '/admin/channels', label: 'Channels OAuth', requirePermission: 'admin:domains:view' },
      { to: '/admin/social-media-channels', label: 'Social Media Channels', requirePermission: 'admin:settings:view' },
      { to: '/admin/channels/telegram', label: 'Telegram', requirePermission: 'admin:settings:manage' },
      { to: '/admin/channels/email/inbound', label: 'Email Inbound', requirePermission: 'admin:integrations:view' },
      { to: '/admin/channels/email/outbound', label: 'Email Outbound', requirePermission: 'admin:integrations:view' },
      { to: '/admin/widget-keys', label: 'Widget Keys', requirePermission: 'admin:settings:view' },
      { to: '/admin/channels/sms-gateways', label: 'SMS Gateway', requirePermission: 'admin:domains:view' },
    ],
  },
  {
    sectionLabel: 'Campaign Management',
    icon: <Mail className="w-5 h-5" />,
    items: [
      { to: '/admin/web-contact-management/campaigns', label: 'Campaigns', requirePermission: 'admin:wcm:view' },
    ],
  },
  {
    sectionLabel: 'Productivity Management',
    icon: <Zap className="w-5 h-5" />,
    items: [
      { to: '/admin/productivity-management/rule-management', label: 'Rule Management', requirePermission: 'admin:settings:view' },
      { to: '/admin/productivity-management/automation-rules', label: 'Automation Rules', requirePermission: 'admin:settings:view' },
      { to: '/admin/productivity-management/sla-policies', label: 'SLA Policies', requirePermission: 'admin:settings:view' },
      { to: '/admin/productivity-management/ola-policies', label: 'OLA Policies', requirePermission: 'admin:settings:view' },
      { to: '/admin/productivity-management/ticket-assignment', label: 'Conversation Assignment', requirePermission: 'admin:settings:view' },
      { to: '/admin/productivity-management/scripts', label: 'Scripts', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Client Management',
    icon: <Users className="w-5 h-5" />,
    items: [
      { to: '/admin/client-management/client', label: 'Client', requirePermission: 'admin:settings:view' },
      { to: '/admin/client-management/client-forms', label: 'Client Forms', requirePermission: 'admin:settings:view' },
      { to: '/admin/client-management/client-groups', label: 'Client Groups', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Template Management',
    icon: <FileText className="w-5 h-5" />,
    items: [
      { to: '/admin/template-management/email-templates', label: 'Email Templates', requirePermission: 'admin:settings:view' },
      { to: '/admin/template-management/sms-templates', label: 'SMS Templates', requirePermission: 'admin:settings:view' },
      { to: '/admin/template-management/whatsapp-templates', label: 'WhatsApp Templates', requirePermission: 'admin:settings:view' },
      { to: '/admin/template-management/web-templates', label: 'Web Templates', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Dialer + Outreach',
    icon: <Megaphone className="w-5 h-5" />,
    items: [
      { to: '/admin/campaigns', label: 'Campaigns', requirePermission: 'admin:campaigns:view' },
      { to: '/admin/contacts', label: 'Contacts', requirePermission: 'admin:calls:view' },
      { to: '/admin/callbacks', label: 'Callbacks', requirePermission: 'admin:calls:view' },
      { to: '/admin/dnc', label: 'DNC', requirePermission: 'admin:campaigns:view' },
    ],
  },
  {
    sectionLabel: 'Recordings',
    icon: <Mic className="w-5 h-5" />,
    items: [
      { to: '/admin/recordings', label: 'Recordings', requirePermission: 'admin:recordings:view' },
      { to: '/admin/recording-settings', label: 'Recording Settings', requirePermission: 'admin:recordings:view' },
    ],
  },
  /*
  {
    // ITEM 115 / §9 — restored. This section was commented out with no
    // explanation, leaving TEN working admin pages reachable only by typing the
    // URL. Every route below exists in router.tsx and is gated on
    // `conversations:admin` — verified individually before uncommenting.
    //
    // The icon is JSX, not the `'pi pi-cog'` string the commented block used:
    // every sibling section passes a ReactNode, and a bare string would have
    // typechecked (strings are valid children) while rendering the literal text
    // "pi pi-cog" next to the section label.
    sectionLabel: 'Conversation Engine',
    icon: <Cog className="w-5 h-5" />,
    requirePermission: 'conversations:admin',
    items: [
      { to: '/admin/conversation-engine/sla', label: 'SLA Policies' },
      { to: '/admin/conversation-engine/ola', label: 'OLA Policies' },
      { to: '/admin/conversation-engine/business-hours', label: 'Business Hours' },
      { to: '/admin/conversation-engine/holidays', label: 'Holidays' },
      { to: '/admin/conversation-engine/routing-rules', label: 'Routing Rules' },
      { to: '/admin/conversation-engine/automation-rules', label: 'Automation Rules' },
      { to: '/admin/conversation-engine/escalation', label: 'Escalation' },
      { to: '/admin/conversation-engine/notification-templates', label: 'Notification Templates' },
      { to: '/admin/conversation-engine/custom-fields', label: 'Custom Fields' },
      { to: '/admin/conversation-engine/approval-workflows', label: 'Approval Workflows' },
    ],
  },
  */
  {
    sectionLabel: 'Reports + Dashboards',
    icon: <BarChart3 className="w-5 h-5" />,
    requirePermission: 'admin:reports:view',
    items: [
      { to: '/admin/reports/scheduled', label: 'Scheduled Reports', requirePermission: 'admin:reports:view' },
      { to: '/admin/dashboards', label: 'Dashboards', requirePermission: 'admin:dashboard:view' },
      { to: '/admin/csat/config', label: 'CSAT Config' },
      { to: '/admin/csat/results', label: 'CSAT Results' },
    ],
  },
  {
    sectionLabel: 'Content Management',
    icon: <BookOpen className="w-5 h-5" />,
    requirePermission: 'admin:kb:view',
    items: [
      { to: '/admin/knowledge-base/categories', label: 'Categories', requirePermission: 'admin:kb:view' },
      { to: '/admin/knowledge-base/articles', label: 'Content', requirePermission: 'admin:kb:view' },
      { to: '/admin/portal-config', label: 'Portal Configuration', requirePermission: 'admin:portal:manage' },
      { to: '/admin/form-creation', label: 'Form Creation', requirePermission: 'admin:portal:manage' },
      { to: '/admin/chat-widget', label: 'Chat Widget', requirePermission: 'admin:portal:manage' },
      { to: '/admin/design-library', label: 'Design Library', requirePermission: 'admin:portal:manage' },
      { to: '/admin/chatbot-flow', label: 'Chatbot Flow', requirePermission: 'admin:portal:manage' },
      { to: '/admin/content-permissions', label: 'Content Permissions', requirePermission: 'roles:manage' },
    ],
  },
  {
    // Hybrid Integration Layer — cloud ↔ on-premises systems (IVRS, core systems).
    sectionLabel: 'Hybrid Integration',
    icon: <Cable className="w-5 h-5" />,
    items: [
      { to: '/admin/hybrid-integration/overview', label: 'Overview', requirePermission: 'admin:settings:view' },
      { to: '/admin/hybrid-integration/connections', label: 'Connections', requirePermission: 'admin:settings:view' },
      { to: '/admin/hybrid-integration/api-clients', label: 'API Clients', requirePermission: 'admin:settings:view' },
      { to: '/admin/hybrid-integration/events', label: 'Event Deliveries', requirePermission: 'admin:settings:view' },
      { to: '/admin/hybrid-integration/call-contexts', label: 'IVR Call Contexts', requirePermission: 'admin:settings:view' },
      { to: '/admin/hybrid-integration/logs', label: 'Request Log', requirePermission: 'admin:settings:view' },
    ],
  },
  {
    sectionLabel: 'Compliance',
    icon: <Shield className="w-5 h-5" />,
    items: [
      { to: '/admin/compliance', label: 'Reports', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/audit-log', label: 'Audit Log', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/retention-policies', label: 'Retention', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/gdpr-requests', label: 'GDPR Requests', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/baa', label: 'BAA', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/access-reviews', label: 'Access Reviews', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/sox-controls', label: 'SOX Controls', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/sso', label: 'SSO', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/data-classifications', label: 'Data Classification', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/tcpa', label: 'TCPA', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/consent-records', label: 'Consent Records', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/exports', label: 'Exports', requirePermission: 'admin:audit:view' },
      { to: '/admin/compliance/data-breaches', label: 'Data Breaches', requirePermission: 'admin:audit:view' },
    ],
  },
]

export default function AdminLayout(): JSX.Element {
  const permissions = useAuthStore((s) => s.user?.permissions ?? EMPTY_PERMS)
  const roles = useAuthStore((s) => s.user?.roles ?? EMPTY_PERMS)
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false)
  const isPlatformAdmin = roles.some((r) => r === 'PLATFORM_ADMIN' || r === 'PLATFORM_SUPER_ADMIN')
  const canBypass = isSuperAdmin || isPlatformAdmin
  const location = useLocation()
  const navigate = useNavigate()
  const clearSession = useAuthStore((s) => s.clearSession)
  const user = useAuthStore((s) => s.user)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  /*
    The rail scrolls once the section list outgrows the viewport, and a scroll
    container clips on BOTH axes — `overflow-y: auto` forces `overflow-x` to
    `auto` too, so a tooltip positioned `left-full` would be cut off at the
    56px rail edge. The tooltip is therefore rendered once, outside the
    scroller, at fixed coordinates measured from the hovered button.
  */
  const [railTip, setRailTip] = useState<{ label: string; x: number; y: number } | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    try {
      // Call backend logout to revoke tokens and clear cookies
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      // Always clear local session and redirect, even if API call fails
      clearSession()
      navigate(loginPathFor(location.pathname))
      setMenuOpen(false)
    }
  }

  const initials = user?.email ? user.email.charAt(0).toUpperCase() : '?'

  const canAccessLeaf = useCallback(
    (leaf: NavLeaf): boolean =>
      canBypass ||
      !leaf.requirePermission ||
      permissions.includes('*') ||
      permissions.includes(leaf.requirePermission),
    [permissions, canBypass]
  )

  const allowedSections = useMemo(() => {
    return NAV.filter((sec) => {
      if (canBypass) return true
      // Check section-level permission
      if (sec.requirePermission) {
        const required = Array.isArray(sec.requirePermission)
          ? sec.requirePermission
          : [sec.requirePermission]
        if (!permissions.includes('*') && !required.some((p) => permissions.includes(p))) {
          return false
        }
      }
      // Check if any items in the section are accessible
      return sec.items.some(canAccessLeaf)
    })
  }, [permissions, canBypass, canAccessLeaf])

  /*
    Flattened side-panel leaves for the header search. Built from the already
    permission-filtered sections so the search can never surface a page the
    user cannot open.
  */
  const searchItems = useMemo<NavSearchItem[]>(
    () =>
      allowedSections.flatMap((sec) =>
        sec.items
          .filter(canAccessLeaf)
          .map((leaf) => ({ to: leaf.to, label: leaf.label, section: sec.sectionLabel }))
      ),
    [allowedSections, canAccessLeaf]
  )

  // Determine active section based on current path
  const activeSection = useMemo(() => {
    // Exact match first
    for (const section of allowedSections) {
      if (section.items.some(item => location.pathname === item.to || location.pathname.startsWith(item.to + '/'))) {
        return section
      }
    }
    // Fallback if none match
    return allowedSections[0]
  }, [location.pathname, allowedSections])

  return (
    <div className="h-screen w-full overflow-hidden bg-[#f8f9fa] flex flex-col">
        {/* HEADER */}
        <header className="bg-white border-b border-gray-100 h-[60px] flex items-center justify-between px-6 relative z-50 shrink-0">
          {/* Left — Logo + Search */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src={voz360Logo} alt="Voz360" className="h-8 w-auto object-contain" />
              <span className="text-sm font-semibold text-gray-900 tracking-tight">Admin</span>
            </div>

            {/* Search — filters the side-panel menu names, no modal */}
            <div className="hidden md:flex items-center">
              <NavSearch items={searchItems} label="Search navigation" placeholder="Search menu..." />
            </div>
          </div>

          {/* Right — Notifications + Profile */}
          <div className="flex items-center gap-1">
            {/* Notification */}
            <button className="relative h-9 w-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all">
              <Bell className="h-[18px] w-[18px]" />
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#f6931c] ring-2 ring-white" />
            </button>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-100 mx-2" />

            {/* Profile dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2.5 h-9 pl-1.5 pr-2 rounded-lg hover:bg-gray-50 transition-all"
              >
                {/* Avatar */}
                <div className="h-7 w-7 rounded-full bg-[#3454cf] flex items-center justify-center text-white text-xs font-semibold">
                  {initials}
                </div>
                <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown */}
              {menuOpen && (
                <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 overflow-hidden z-50 py-1">
                  {/* User info */}
                  <div className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{user?.email || 'User'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Administrator</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-50 mx-3" />

                  {/* Timezone */}
                  <div className="p-1.5">
                    <TimezoneMenuItem />
                  </div>

                  {/* Security / MFA */}
                  <div className="p-1.5">
                    <button
                      onClick={() => {
                        setMenuOpen(false)
                        navigate('/admin/mfa')
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Shield className="h-4 w-4 text-gray-500" />
                      <span>Multi-Factor Authentication</span>
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-50 mx-3" />

                  {/* Logout */}
                  <div className="p-1.5">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* SIDEBAR AND CONTENT */}
        <div className="flex flex-1 overflow-hidden">
          {/* RAIL SIDEBAR */}
          <div
            className="w-[56px] flex-shrink-0 bg-[#f8f9fa] border-r border-gray-100 flex flex-col items-center py-3 z-30 h-full min-h-0 overflow-y-auto overflow-x-hidden nav-scroll"
            onScroll={() => setRailTip(null)}
          >
            {allowedSections.map((section) => {
              const isActive = activeSection?.sectionLabel === section.sectionLabel
              const showTip = (event: { currentTarget: HTMLElement }): void => {
                const r = event.currentTarget.getBoundingClientRect()
                setRailTip({ label: section.sectionLabel, x: r.right + 12, y: r.top + r.height / 2 })
              }
              return (
                <div key={section.sectionLabel} className="relative shrink-0 mb-1">
                  <button
                    onClick={() => {
                      const firstItem = section.items[0]
                      if (firstItem) navigate(firstItem.to)
                    }}
                    onMouseEnter={showTip}
                    onMouseLeave={() => setRailTip(null)}
                    onFocus={showTip}
                    onBlur={() => setRailTip(null)}
                    className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-[#3454cf]/10 text-[#3454cf] shadow-[0_0_0_1px_rgba(52,84,207,0.15)_inset]'
                        : 'text-gray-400 hover:bg-white hover:text-gray-600 hover:shadow-sm'
                    }`}
                    title={section.sectionLabel}
                  >
                    {section.icon}
                  </button>
                  {/* Active left pill — orange accent */}
                  {isActive && (
                    <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full" style={{ background: '#f6931c' }} />
                  )}
                </div>
              )
            })}
          </div>

          {/* Rail tooltip — fixed so the scrolling rail cannot clip it */}
          {railTip && (
            <div
              role="presentation"
              className="fixed -translate-y-1/2 px-3 py-2 bg-gray-900/95 backdrop-blur-sm text-white text-xs whitespace-nowrap rounded-lg z-[9999] font-medium shadow-xl pointer-events-none"
              style={{ left: railTip.x, top: railTip.y }}
            >
              {railTip.label}
              <div className="absolute left-0 top-1/2 -translate-x-[3px] -translate-y-1/2 w-[6px] h-[6px] bg-gray-900/95 rotate-45" />
            </div>
          )}

          {/* SUB-NAVIGATION */}
          {activeSection && (
            <div className="w-[250px] flex-shrink-0 bg-white border-r border-gray-100 flex flex-col z-20 overflow-hidden h-full">
              {/* Header */}
              <div className="px-5 pt-5 pb-4 border-b border-gray-50">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#f6931c]/10 text-[#f6931c]">
                    {activeSection.icon}
                  </div>
                  <div>
                    <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">
                      {activeSection.sectionLabel}
                    </h2>
                    <div className="mt-0.5 h-[2px] w-8 rounded-full" style={{ background: '#f6931c' }} />
                  </div>
                </div>
              </div>

              {/* Nav items */}
              {/*
                aria-label: with more than one navigation landmark on the page
                (the section rail plus this leaf panel), a screen reader
                announces each simply as "navigation" and the user cannot tell
                them apart. The label names WHICH nav this is, and changes with
                the active section so it stays accurate.
              */}
              <nav
                aria-label={`Admin ${activeSection.sectionLabel} navigation`}
                className="flex flex-col gap-0.5 p-3 flex-1 overflow-y-auto min-h-0 nav-scroll"
              >
                {activeSection.items.filter(canAccessLeaf).map((leaf) => (
                  <NavLink
                    key={leaf.to}
                    to={leaf.to}
                    end={true}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-[#3454cf]/6 text-[#3454cf] font-semibold shadow-[0_0_0_1px_rgba(52,84,207,0.08)_inset]'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 font-medium'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className="truncate flex-1">{leaf.label}</span>
                        {/* Orange dot for active */}
                        {isActive && (
                          <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#f6931c' }} />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </nav>

              {/* Bottom info card with orange accent */}
              <div className="mt-auto mx-3 mb-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #3454cf, #f6931c)' }} />
                <div className="flex items-center gap-2 pt-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-[12px] text-gray-500 font-medium">System Online</span>
                </div>
              </div>
            </div>
          )}

          {/* MAIN CONTENT AREA */}
          <main className="flex-1 min-w-0 overflow-y-auto">
            <div className="p-6">
              <Outlet />
            </div>
          </main>
        </div>
        <BrowserNotificationPrompt />
      </div>
  )
}
